import React, { ChangeEvent, Fragment, FormEvent } from "react";
import ReactModal from "react-modal";

import { DateTime } from "luxon";

import Calendar2, { ICalendar2ButtonCallbacks, ICalendarDetailsInfo } from "../Calendar2";
import DelayInput from "../DelayInput";
import ValidateInput from "../ValidateInput";

import { V1, V2 } from "../../Api";
import { formatTimeLocal, formatName, formatDateLocal, WEEKDAYS } from "../../Utilities";
import withTitle from "../withTitle";
import withAuth from "../withAuth";
import { IAuthProps, WellKnownGroups } from "../AuthContext";
import { RouteComponentProps } from "react-router";
import ViewToolbar from "../ViewToolbar";
import ToolbarMenuItem from "../ToolbarMenuItem";
import TabControl from "../TabControl";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import HasRight from "../HasRight";
import withProps from "../withProps";

type EditCoverageMode = "ranges" | "slots" | "details";

interface IScheduleCoverageViewState {
    events: any;
    isLoading: boolean | string;
    isDialogSubmitting: boolean | string;
    isAddCoverageDialogVisible: boolean;
    isEditCalendarDialogVisible: boolean;
    isDeletionDialogOpen: boolean;
    hasDialogChanged: boolean;
    disallowDialogSave: boolean;

    canEditCalendar: boolean;
    month: string;

    displayAfter: DateTime;
    displayBefore: DateTime;

    searchCoverage: string;

    calCoverage: string;
    calItem: any;

    calEnd: string;
    calStart: string;
    calEmployee: string;
    detailsMode: "add" | "edit";
    detailsButtonRender: (callbacks: ICalendar2ButtonCallbacks) => React.ReactChild[];


    employees: any[];
    coverages: any[];
    locations: any[];

    dayDetailsEntries: any[];

    addCoverageWeekStart?: string;
    addCoverageWeekEnd?: string;
    addCoverageId: string;

    editCoverageId: string;
    editCoverageLoaded: boolean;
    editCoverageSlotRanges: any[];
    editCoverageSelectedSlotIndex: string;
    editCoverageSelectedSlot: any | null;
    editCoverageRanges: any[];
    editCoverageMode: EditCoverageMode;
    editCoverageDefaultTab: number;
    editCoverageName: string;
    editCoverageLocation: string;

    newSlotCounter: number;
}

interface IScheduleCoverageViewProps extends IAuthProps, RouteComponentProps<any> {
    group: string;
}

class ScheduleCoverageView extends React.Component<IScheduleCoverageViewProps, IScheduleCoverageViewState> {
    constructor(props: Readonly<IScheduleCoverageViewProps>) {
        super(props);

        const today = DateTime.local();

        const state: IScheduleCoverageViewState = {
            events: {},
            isLoading: false,
            isDialogSubmitting: false,
            isAddCoverageDialogVisible: false,
            isEditCalendarDialogVisible: false,
            isDeletionDialogOpen: false,
            hasDialogChanged: false,
            disallowDialogSave: false,

            canEditCalendar: props.authContext.hasRight("sched:c:assign"),
            month: today.startOf("month").toFormat("yyyy-MM"),

            displayBefore: today.endOf("month"),
            displayAfter: today.startOf("month"),

            searchCoverage: "",

            calCoverage: "",
            calItem: {},

            employees: [],
            coverages: [],
            locations: [],

            calEnd: "",
            calStart: "",
            calEmployee: "",
            detailsMode: "edit",
            detailsButtonRender: this.renderDetailsButtonsEdit,

            dayDetailsEntries: [],

            addCoverageId: "",
            editCoverageId: "",
            editCoverageLoaded: false,

            editCoverageSlotRanges: [],
            editCoverageSelectedSlotIndex: "",
            editCoverageSelectedSlot: null,
            editCoverageRanges: [],
            editCoverageMode: "ranges",
            editCoverageDefaultTab: 0,
            editCoverageName: "",
            editCoverageLocation: "",

            newSlotCounter: 0,
        };

        this.state = state;

        this.handleElementChange = this.handleElementChange.bind(this);
        this.displayRangeChanged = this.displayRangeChanged.bind(this);
        this.detailsRender = this.detailsRender.bind(this);
        this.handleDialogChange = this.handleDialogChange.bind(this);
        this.handleDialogCoverageSlotChange = this.handleDialogCoverageSlotChange.bind(this);
        this.handleItemEdit = this.handleItemEdit.bind(this);
        this.beforeShowAddInterface = this.beforeShowAddInterface.bind(this);
        this.handleBeforeItemEdit = this.handleBeforeItemEdit.bind(this);
        this.renderDayDetailsButtons = this.renderDayDetailsButtons.bind(this);
        this.showCoverageAddDialog = this.showCoverageAddDialog.bind(this);
        this.requestCoverageDialogClose = this.requestCoverageDialogClose.bind(this);
        this.handleAddCoverageCalendarClick = this.handleAddCoverageCalendarClick.bind(this);
        this.addCoverage = this.addCoverage.bind(this);
        this.showCalendarEditDialog = this.showCalendarEditDialog.bind(this);
        this.requestCalendarEditDialogClose = this.requestCalendarEditDialogClose.bind(this);
        this.saveCoverageInfo = this.saveCoverageInfo.bind(this);
        this.addCoverageRange = this.addCoverageRange.bind(this);
        this.handleCoverageEditTabChanged = this.handleCoverageEditTabChanged.bind(this);
        this.deleteCoverage = this.deleteCoverage.bind(this);
        this.promptDeleteCoverage = this.promptDeleteCoverage.bind(this);
        this.closeDeleteDialog = this.closeDeleteDialog.bind(this);
    }

    public async componentDidMount() {
        try {
            const [coverages, employees, locations] = await Promise.all([
                V2.get<any[]>(`schedule/coverage/types?group=${this.props.group}`),
                V1.get<any[]>(`employees/summary?group=${this.props.group}`),
                V2.get<any[]>("locations"),
            ]);

            this.setState({
                coverages,
                employees: employees.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),
                locations: locations.sort((a, b) => a.name.localeCompare(b.name)),
            });
        }
        catch (e) {
            //
        }
    }

    public render() {
        return (
            <div className="viewer-root viewer-full full-height">
                <ViewToolbar>
                    <ToolbarMenuItem right="sched:c:write">
                        <button className="icon-pencil" title="Edit calendar..." onClick={this.showCalendarEditDialog} />
                    </ToolbarMenuItem>
                    <ToolbarMenuItem right="sched:c:write">
                        <button className="icon-plus" title="Add coverage to calendar..." onClick={this.showCoverageAddDialog} />
                    </ToolbarMenuItem>
                </ViewToolbar>
                {this.state.isAddCoverageDialogVisible ? this.renderAddCoverageDialog() : null}
                {this.state.isEditCalendarDialogVisible ? this.renderCalendarEditDialog() : null}
                <DeletionConfirmationDialog isOpen={this.state.isDeletionDialogOpen} description={null} onConfirm={this.deleteCoverage} onRequestClose={this.closeDeleteDialog}>
                    <div>
                        Do you really want to delete the coverage <b>{this.state.editCoverageName}</b>?<br />
                        <br />
                        <span className="error-text">Warning:</span> This will <span className="error-text">completely</span> remove the coverage <em>and</em> assignments from the calendar across <span className="error-text">ALL</span> months and years!
                    </div>
                </DeletionConfirmationDialog>
                <div className="filter-control-box">
                    <DelayInput type="month" className="fill-space" timeout={500} value={this.state.month} name="month" onChangeDelayed={this.handleElementChange} />
                    <select value={this.state.searchCoverage} name="searchCoverage" onChange={this.handleElementChange} disabled={this.state.isLoading === true}>
                        <option value="">All Coverages</option>
                        {this.state.coverages.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                </div>
                <div className="spinner-overlay-root full-height">
                    <Calendar2 className="full-height" displayMonth={this.state.month} highlightDate={new Date()} showDetailsOnClick={true} showButtonsOnDetails={true} allowEdit={this.state.canEditCalendar} disableDelete={true} selectionMode="day" detailsAddRender={this.detailsRender} events={this.state.events} onDisplayRangeChanged={this.displayRangeChanged} onItemAdd={this.handleItemEdit} onBeforeShowAdd={this.beforeShowAddInterface} onBeforeEdit={this.handleBeforeItemEdit} onItemEdit={this.handleItemEdit} detailsItemRender={this.renderDetailsItem} detailsButtonsRender={this.state.detailsButtonRender} dayDetailsButtonsRender={this.renderDayDetailsButtons} />
                    {
                        this.state.isLoading ? (
                            <div className="spinner-overlay">
                                {
                                    (typeof this.state.isLoading === "string") ?
                                        <span className="error-text-big">{this.state.isLoading}</span> :
                                        <div className="lds-dual-ring-big" />
                                }
                            </div>
                        ) : ""
                    }
                </div>
            </div>
        );
    }

    private renderAddCoverageDialog() {
        return <ReactModal
            isOpen={this.state.isAddCoverageDialogVisible} onRequestClose={this.requestCoverageDialogClose}
            overlayClassName="modal-overlay" className="modal-content" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Add coverage">
            <form className="input-form" onSubmit={this.addCoverage}>
                <div className="info-block">
                    <header>
                        <h3>Add Coverage</h3>
                    </header>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-coverage-type">Coverage</label>
                            <select id="frm-coverage-type" name="addCoverageId" value={this.state.addCoverageId} onChange={this.handleDialogChange}>
                                <option />
                                {this.state.coverages.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-coverage-week">Select week to start</label>
                            <Calendar2 selectionMode="week" displayMonth={this.state.month} highlightDateStart={this.state.addCoverageWeekStart} highlightDateEnd={this.state.addCoverageWeekEnd} onDaySelected={this.handleAddCoverageCalendarClick} />
                        </div>
                    </section>
                    {this.state.isDialogSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isDialogSubmitting === "string"
                            ? <div className="error-text">{this.state.isDialogSubmitting}</div>
                            : null
                    }
                    &nbsp;
                    <div className="action-panel-dialog">
                        <button type="submit" className="button green push-right text-with-icon" disabled={!this.state.addCoverageId || !this.state.addCoverageWeekStart}><i className="icon-check" /><span>Add</span></button>
                        <button type="button" className="button" onClick={this.requestCoverageDialogClose}>Cancel</button>
                    </div>
                </div>
            </form>
        </ReactModal>;
    }

    private renderCalendarEditDialog() {
        return <ReactModal
            isOpen={this.state.isEditCalendarDialogVisible} onRequestClose={() => this.requestCalendarEditDialogClose()}
            overlayClassName="modal-overlay" className="modal-content modal-content-medium" portalClassName="modal-portal"
            bodyOpenClassName="modal-open"
            contentLabel="Edit calendar">
            <form className="input-form" onSubmit={this.saveCoverageInfo}>
                <div className="info-block">
                    <header>
                        <h3>Edit Calendar</h3>
                    </header>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-coverage-type">Select a Coverage</label>
                            <select id="frm-coverage-type" name="editCoverageId" value={this.state.editCoverageId} disabled={this.state.hasDialogChanged} onChange={e => this.handleDialogChange(e, this.selectedCoverageChanged)}>
                                <option />
                                {this.state.coverages.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                <HasRight right="sched:c:create">
                                    <option value="new">Create new coverage...</option>
                                </HasRight>
                            </select>
                        </div>
                    </section>
                    <br />
                    {
                        this.state.editCoverageLoaded
                            ? <TabControl headers={["Active Ranges", "Slots", "Details"]} defaultSelectedIndex={this.state.editCoverageDefaultTab} disabled={this.state.hasDialogChanged} onTabChanged={this.handleCoverageEditTabChanged}>
                                <div>
                                    <section>
                                        <div className="input-form-group">
                                            <label>Active Ranges</label>
                                            <ul className="list-basic list-hover">
                                                {this.state.editCoverageRanges.map((a, i) => <li key={i} className="flex">
                                                    {a.isEdit
                                                        ? <Fragment>
                                                            <input type="date" className="lowpad" value={a.start || ""} onChange={e => this.changeRangeValue(e, a, "start")} /> to <input type="date" className="lowpad" value={a.end || ""} onChange={e => this.changeRangeValue(e, a, "end")} /> <button type="button" className="button btn-outline small btn-inline-center noflex" title="Save" onClick={e => this.toggleEditRange(a)}><i className="icon-check" /></button>
                                                        </Fragment>
                                                        : <Fragment>
                                                            {formatDateLocal(a.start)} &mdash; {a.end ? formatDateLocal(a.end) : "Onward"} <span className="spring" />
                                                            <div className="control-group mobileflex">
                                                                <button type="button" className="button btn-light smaller btn-inline-center flex" title="Edit" onClick={e => this.toggleEditRange(a)}><i className="icon-pencil" /></button>
                                                                <button type="button" className="button btn-light smaller btn-inline-center flex red" title="Remove Range" onClick={() => this.deleteCoverageRange(i)}><i className="icon-times" /></button>
                                                            </div>
                                                        </Fragment>}
                                                </li>)}
                                            </ul>
                                            <div>
                                                <button type="button" className="button btn-outline text-with-icon" onClick={this.addCoverageRange}><i className="icon-plus" /><span>Add range</span></button>
                                            </div>
                                        </div>
                                    </section>
                                </div>
                                <div>
                                    <section>
                                        <select id="frm-coverage-slot" name="editCoverageSelectedSlotIndex" value={this.state.editCoverageSelectedSlotIndex} onChange={e => this.handleDialogChange(e, this.selectedSlotChanged)}>
                                            <option value="">Select a slot...</option>
                                            {this.state.editCoverageSlotRanges.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                            <option value="new">Create new slots...</option>
                                        </select>
                                    </section>
                                    {this.state.editCoverageSelectedSlot
                                        ? <Fragment>
                                            <section>
                                                <div className="input-form-group">
                                                    <label htmlFor="frm-coverage-slot-start">Start date</label>
                                                    <DelayInput type="date" timeout={300} id="frm-coverage-slot-start" name="start" value={this.state.editCoverageSelectedSlot.start} onChangeDelayed={e => this.handleDialogCoverageSlotChange(e, this.selectedSlotDateChanged)} />
                                                </div>
                                            </section>
                                            <section>
                                                <div className="input-form-group">
                                                    <label>Days & Times</label>
                                                    <ul className="list-basic">
                                                        {
                                                            [0, 1, 2, 3, 4, 5, 6].map(day => <li key={day} className="flex">
                                                                <div>{WEEKDAYS[day]}</div>
                                                                <div className="control-group">
                                                                    <input type="time" className="lowpad" name="startTime" value={this.state.editCoverageSelectedSlot.items[day].startTime} onChange={e => this.handleCoverageSlotDayChange(e, day)} />
                                                                    <input type="time" className="lowpad" name="endTime" value={this.state.editCoverageSelectedSlot.items[day].endTime} onChange={e => this.handleCoverageSlotDayChange(e, day)} />
                                                                </div>
                                                            </li>)
                                                        }
                                                    </ul>
                                                </div>
                                            </section>
                                        </Fragment>
                                        : null}
                                </div>
                                <div>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-coverage-name">Coverage Name</label>
                                            <input type="text" id="frm-coverage-name" name="editCoverageName" value={this.state.editCoverageName} onChange={e => this.handleDialogChange(e, this.setDialogChanged)} />
                                        </div>
                                    </section>
                                    <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-coverage-location">Bind to location schedule</label>
                                            <select id="frm-coverage-location" name="editCoverageLocation" value={this.state.editCoverageLocation} onChange={e => this.handleDialogChange(e, this.setDialogChanged)}>
                                                <option />
                                                {this.state.locations.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                            </select>
                                            <p>
                                                <small><em>This will constrain slots to the days & times of the location as specified by the office schedule.</em></small>
                                            </p>
                                        </div>
                                    </section>
                                    <HasRight right="sched:c:delete">
                                        {!this.state.editCoverageId.startsWith("new")
                                            ? <section>
                                                <button type="button" className="button btn-light red text-with-icon" onClick={this.promptDeleteCoverage}><i className="icon-trash" /><span>Delete coverage...</span></button>
                                            </section>
                                            : null}
                                    </HasRight>
                                    {this.state.hasDialogChanged ? <p><em>You must save before changing ranges or slots.</em></p> : null}
                                </div>
                            </TabControl>
                            : null
                    }
                    {this.state.isDialogSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isDialogSubmitting === "string"
                            ? <div className="error-text">{this.state.isDialogSubmitting}</div>
                            : null
                    }
                    &nbsp;
                    <div className="action-panel-dialog">
                        {this.state.hasDialogChanged ? <button type="submit" className="button green push-right text-with-icon" disabled={!this.state.hasDialogChanged || this.state.disallowDialogSave === true}><i className="icon-check" /><span>Save</span></button> : null}
                        <button type="button" className={"button" + (!this.state.hasDialogChanged ? " push-right" : "")} onClick={() => this.requestCalendarEditDialogClose(true)}>{this.state.hasDialogChanged ? "Cancel" : "Close"}</button>
                    </div>
                </div>
            </form>
        </ReactModal>;
    }

    private async loadData() {
        try {
            this.setState({
                events: {},
                isLoading: true,
            });

            const beginDate = this.state.displayAfter;
            const endDate = this.state.displayBefore.plus({ millisecond: 1 });

            const parameters = [
                `group=${this.props.group}`,
                `after=${encodeURIComponent(beginDate.toISO())}`,
                `before=${encodeURIComponent(endDate.toISO())}`
            ];

            if (this.state.searchCoverage) {
                parameters.push(`coverage=${this.state.searchCoverage}`);
            }

            const data = await V2.get<any[]>(`schedule/coverage?${parameters.join("&")}`);
            const monthBase = DateTime.fromISO(this.state.month);
            const events: {
                [date: number]: Array<{
                    id: string,
                    title: string,
                    type?: string,
                    length?: number,
                    item?: any,
                }>;
            } = {};
            for (const item of data) {
                const dayDiff = DateTime.fromISO(item.date).diff(monthBase, "days").days + 1;
                const runLength = 1;

                if (!events[dayDiff])
                    events[dayDiff] = [];

                events[dayDiff].push({
                    id: dayDiff + item.id,
                    title: item.employee
                        ? `${item.coverage.name} - ${formatName(item.employee.name, true)}`
                        : `${item.coverage.name}`,
                    type: item.employee ? "green" : "red",
                    length: runLength,
                    item
                });
            }

            this.setState({
                events,
                isLoading: false
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Unable to load schedule"
            });
        }
    }

    private handleElementChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        if (name === "month" && !value) {
            return;
        }

        this.setState({
            [name]: value
        } as any, () => {
            // this.saveSearchState();
            if (name !== "month") {
                this.loadData();
            }
        });
    }

    private displayRangeChanged(minDate: string, maxDate: string) {
        this.setState({
            displayBefore: DateTime.fromISO(maxDate).toLocal(),
            displayAfter: DateTime.fromISO(minDate).toLocal()
        }, () => this.loadData());
    }

    private handleDialogChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, callback?: () => void) {
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            [name]: value
        } as any, callback);
    }

    private handleDialogCoverageSlotChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, callback?: () => void) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        const currentCoverage = this.state.editCoverageSelectedSlot;
        (currentCoverage as any)[name] = value;

        this.setState({
            hasDialogChanged: true,

            editCoverageSelectedSlot: currentCoverage
        } as any, callback);
    }

    private setDialogChanged() {
        this.setState({
            hasDialogChanged: true
        });
    }

    private detailsRender(state: any) {
        if (this.state.detailsMode === "add") {
            return this.detailsAddRender(state);
        }

        // Single-day edit:
        return (
            <div className="input-form">
                <div className="info-block">
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-coverage" className="required">Coverage</label>
                            <ValidateInput type="select" id="frm-cal-coverage" name="calCoverage" value={this.state.calCoverage} disabled={state.isEdit} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true}>
                                <option />
                                {this.state.coverages.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </ValidateInput>
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-start" className="required">Start Time</label>
                            <ValidateInput type="time" id="frm-cal-start" name="calStart" value={this.state.calStart} disabled={state.isEdit} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-end" className="required">End Time</label>
                            <ValidateInput type="time" id="frm-cal-end" name="calEnd" value={this.state.calEnd} disabled={state.isEdit} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={true} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="frm-cal-employee" className="required">Employee</label>
                            <ValidateInput type="select" id="frm-cal-employee" name="calEmployee" value={this.state.calEmployee} onChange={this.handleDialogChange} autoComplete="off_please_chrome" isRequired={false}>
                                <option value="">- Not Assigned -</option>
                                {this.state.employees.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                            </ValidateInput>
                        </div>
                    </section>
                </div>
            </div>
        );
    }

    private detailsAddRender(state: any) {
        return "Hi";
    }

    private beforeShowAddInterface() {
        return new Promise<void>((resolve, reject) => {
            this.setState({
                calCoverage: this.state.searchCoverage,
                calEmployee: "",
                calStart: "",
                calEnd: "",
                detailsMode: "add",
                detailsButtonRender: this.renderDetailsButtonsAdd,
            }, () => resolve());
        });
    }

    private async handleItemEdit(date: string, button: string) {
        if (!this.state.calItem) {
            return;
        }

        const itemData = {
            employeeId: this.state.calEmployee,
        };

        let dt = DateTime.fromISO(`${date}T${this.state.calStart}`);

        // No try..catch because the calendar dialog handles it
        await V2.put(`schedule/coverage/${this.state.calCoverage}/${dt.toFormat("yyyy-MM-dd")}?mode=single`, itemData);
        this.loadData();
    }

    private async handleBeforeItemEdit(e: any) {
        if (!e || !e.item) {
            return false;
        }

        this.setState({
            detailsMode: "edit",
            detailsButtonRender: this.renderDetailsButtonsEdit,
            calCoverage: e.item.coverage.id,
            calEmployee: e.item.employee ? e.item.employee.id : "",
            calItem: e.item,

            calStart: e.item.time,
            calEnd: DateTime.fromISO(e.item.time).plus({ hours: e.item.length }).toFormat("HH:mm"),
        });

        return true;
    }

    private renderDetailsItem(item: any) {
        const endTime = DateTime.fromISO(item.item.time).plus({ hours: item.item.length });
        return (
            <span>
                {item.item.coverage.name}<br />
                <small>{formatTimeLocal(item.item.time)} - {formatTimeLocal(endTime)}</small> &mdash; <small>{item.item.employee ? formatName(item.item.employee.name) : "Not assigned"}</small>
            </span>
        );
    }

    private renderDetailsButtonsEdit(callbacks: ICalendar2ButtonCallbacks) {
        return [
            <button key="save" className="button text-with-icon push-right" onClick={callbacks.save}><i className="icon-check" /><span>Save</span></button>
        ];
    }

    private renderDetailsButtonsAdd(callbacks: ICalendar2ButtonCallbacks) {
        return [
            <button key="save" className="button text-with-icon push-right" onClick={callbacks.save}><i className="icon-check" /><span>Save</span></button>
        ];
    }

    private switchMode(mode: "details" | "calendar", info: ICalendarDetailsInfo) {
        this.props.history.push(`${this.props.location.pathname}/details/${info.selectedDateStart.toISODate()}`);
    }

    private renderDayDetailsButtons(info: ICalendarDetailsInfo) {
        return [
            <button key="details" className="button small btn-light btn-center" onClick={() => this.switchMode("details", info)}><i className="icon-list-ul" /></button>
        ];
    }

    private showCoverageAddDialog() {
        this.setState({
            isAddCoverageDialogVisible: true,
            addCoverageWeekStart: undefined,
            addCoverageWeekEnd: undefined,
            addCoverageId: "",
        });
    }

    private requestCoverageDialogClose() {
        this.setState({
            isAddCoverageDialogVisible: false,
        });
    }

    private handleAddCoverageCalendarClick(date: string) {
        const dt = DateTime.fromISO(date);
        const start = dt.plus({ days: 1 }).startOf("week").minus({ days: 1 });

        this.setState({
            addCoverageWeekStart: start.toISODate(),
            addCoverageWeekEnd: start.plus({ days: 6 }).toISODate()
        });
    }

    private async addCoverage(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isDialogSubmitting: true
            });

            const data = {
                start: this.state.addCoverageWeekStart,
                coverageId: this.state.addCoverageId
            };

            await V2.post(`schedule/coverage`, data);

            this.setState({
                isDialogSubmitting: false,
                isAddCoverageDialogVisible: false,
            }, () => this.loadData());
        }
        catch (e) {
            this.setState({
                isDialogSubmitting: e.message || e || "Could not add coverage"
            });
        }
    }

    private showCalendarEditDialog() {
        this.setState({
            isEditCalendarDialogVisible: true,

            editCoverageId: "",
            editCoverageRanges: [],
            editCoverageSlotRanges: [],
            editCoverageLoaded: false,
            editCoverageMode: "ranges",
            editCoverageDefaultTab: 0,
        });
    }

    private requestCalendarEditDialogClose(override?: boolean) {
        if (this.state.hasDialogChanged && override !== true) {
            return;
        }

        const coverages = this.state.coverages;

        this.setState({
            isEditCalendarDialogVisible: false,
            hasDialogChanged: false,
            disallowDialogSave: false,
            coverages: coverages.filter(a => !a.id.startsWith("new")) // Remove any unsaved new coverage
        }, () => this.loadData());
    }

    private async loadCoverageData() {
        try {
            this.setState({
                isDialogSubmitting: true
            });

            const data = await V2.get<any>(`schedule/coverage/types/${this.state.editCoverageId}`);

            const slotKeys = Object.keys(data.slots).sort((a, b) => a.localeCompare(b));
            const slotEntries = [];
            for (var i = 0; i < slotKeys.length; i++) {
                const start = slotKeys[i];
                const next = slotKeys[i + 1];

                const items = data.slots[slotKeys[i]]
                    .reduce((accm: any, current: any) => { accm[current.startDay] = current; return accm; }, {});

                for (let i = 0; i < 7; i++) {
                    if (!items[i]) {
                        items[i] = { startDay: i, startTime: "", endTime: "" };
                    }
                    else {
                        items[i].endTime = DateTime.fromISO(items[i].startTime).plus({ hours: items[i].length }).toFormat("HH:mm");
                    }
                }

                slotEntries.push({
                    id: i.toString(),
                    start: start,
                    name: !!next
                        ? (`${formatDateLocal(start, true)} \u2014 ${formatDateLocal(DateTime.fromISO(next).minus({ days: 1 }), true)}`)
                        : `${formatDateLocal(start, true)} \u2014 Onward`,
                    items: items
                });
            }

            this.setState({
                isDialogSubmitting: false,

                editCoverageLoaded: true,
                editCoverageSlotRanges: slotEntries,
                editCoverageRanges: data.ranges,
                editCoverageSelectedSlotIndex: "",
                editCoverageSelectedSlot: null,
                editCoverageDefaultTab: 0,
                editCoverageName: data.name,
                editCoverageLocation: data.locationId || ""
            });
        }
        catch (e) {
            this.setState({
                isDialogSubmitting: e.message || e || "Could not load coverage information"
            });
        }
    }

    private selectedSlotChanged() {
        let slotIndex = this.state.editCoverageSelectedSlotIndex
        if (slotIndex === "new") {
            const slots = this.state.editCoverageSlotRanges;

            slots.push({
                start: "",
                id: slots.length,
                name: `New slots ${this.state.newSlotCounter + 1} *`,
                items: {
                    0: { startDay: 0, startTime: "", endTime: "" },
                    1: { startDay: 1, startTime: "", endTime: "" },
                    2: { startDay: 2, startTime: "", endTime: "" },
                    3: { startDay: 3, startTime: "", endTime: "" },
                    4: { startDay: 4, startTime: "", endTime: "" },
                    5: { startDay: 5, startTime: "", endTime: "" },
                    6: { startDay: 6, startTime: "", endTime: "" },
                }
            });

            slotIndex = (slots.length - 1).toString();

            this.setState(s => ({
                hasDialogChanged: true,

                editCoverageSlotRanges: slots,
                editCoverageSelectedSlotIndex: slotIndex,
                newSlotCounter: s.newSlotCounter + 1
            }));
        }

        this.setState(s => ({
            editCoverageSelectedSlot: s.editCoverageSlotRanges[+slotIndex]
        }));
    }

    private selectedSlotDateChanged() {
        const currentSlot = this.state.editCoverageSelectedSlot;
        if (!currentSlot.start) {
            return;
        }

        currentSlot.start = DateTime.fromISO(currentSlot.start).plus({ days: 1 }).startOf("week").minus({ days: 1 }).toISODate();
        if (currentSlot.name && !(currentSlot.name as string).endsWith("*")) {
            currentSlot.name += " *";
        }

        this.setState({
            hasDialogChanged: true,

            editCoverageSelectedSlot: currentSlot
        });
    }

    private selectedCoverageChanged() {
        if (this.state.editCoverageId === "new") {
            // New coverage
            const coverages = this.state.coverages;
            const coverage = {
                id: "new" + coverages.length,
                name: "New coverage",
                slots: [],
                ranges: []
            };

            coverages.push(coverage);

            this.setState({
                hasDialogChanged: true,
                coverages: coverages,
                editCoverageId: coverage.id,

                editCoverageLoaded: true,
                editCoverageSlotRanges: coverage.slots,
                editCoverageRanges: coverage.ranges,
                editCoverageSelectedSlotIndex: "",
                editCoverageSelectedSlot: null,
                editCoverageMode: "details",
                editCoverageDefaultTab: 2
            });
        }
        else if (this.state.editCoverageId.startsWith("new")) {
            const coverage = this.state.coverages.find(a => a.id === this.state.editCoverageId);
            if (!coverage) {
                return;
            }

            this.setState({
                editCoverageLoaded: true,
                editCoverageSlotRanges: coverage.slots,
                editCoverageRanges: coverage.ranges,
                editCoverageSelectedSlotIndex: "",
                editCoverageSelectedSlot: null,
                editCoverageMode: "ranges",
                editCoverageDefaultTab: 0,
            });
        }
        else {
            this.loadCoverageData();
        }
    }

    private handleCoverageSlotDayChange(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>, day: number) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        const currentSlot = this.state.editCoverageSelectedSlot;

        currentSlot.items[day][name] = value;
        if (currentSlot.name && !(currentSlot.name as string).endsWith("*")) {
            currentSlot.name += " *";
        }

        this.setState({
            hasDialogChanged: true,

            editCoverageSelectedSlot: currentSlot
        });
    }

    private async saveCoverageInfo(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isDialogSubmitting: true
            });

            const coverageInfo = {
                coverageId: this.state.editCoverageId,
                slots: [] as any[],
                ranges: [] as any[],
            };
            switch (this.state.editCoverageMode) {
                case "slots":
                    this.state.editCoverageSlotRanges.forEach(v => {
                        coverageInfo.slots.push({
                            startDate: v.start,
                            items: Object.keys(v.items)
                                .filter(a => !!v.items[a].startTime)
                                .map((a: any) => ({
                                    startTime: v.items[a].startTime,
                                    startDay: v.items[a].startDay,
                                    length: DateTime.fromISO(v.items[a].endTime).diff(DateTime.fromISO(v.items[a].startTime), "minutes").minutes / 60.0
                                }))
                        });
                    });


                    await V2.put(`schedule/coverage/types/${coverageInfo.coverageId}/slots`, coverageInfo.slots);

                    // Clear all unsaved asterisks
                    const slots = this.state.editCoverageSlotRanges;
                    for (const item of slots) {
                        if (item.name.endsWith(" *")) {
                            item.name = item.name.slice(0, -2);
                        }
                    }

                    this.setState({
                        editCoverageSlotRanges: slots
                    });

                    break;
                case "ranges":
                    this.state.editCoverageRanges
                        .sort((a, b) => a.start.localeCompare(b.start))
                        .forEach(v => {
                            coverageInfo.ranges.push({
                                start: v.start,
                                end: v.end
                            });
                        });

                    await V2.put(`schedule/coverage/types/${coverageInfo.coverageId}/ranges`, coverageInfo.ranges);

                    break;
                case "details":
                    const data = {
                        name: this.state.editCoverageName,
                        locationId: this.state.editCoverageLocation,
                        groupId: this.props.group
                    };

                    const currentCoverage = this.state.coverages.find(a => a.id === coverageInfo.coverageId);
                    if (coverageInfo.coverageId.startsWith("new")) {
                        // New coverage
                        const newInfo = await V2.post<any>(`schedule/coverage/types`, data);

                        if (currentCoverage) {
                            currentCoverage.id = newInfo.id;
                        }

                        this.setState({
                            editCoverageId: newInfo.id
                        });
                    }
                    else {
                        // Update existing coverage
                        await V2.patch(`schedule/coverage/types/${coverageInfo.coverageId}`, data);
                    }

                    if (currentCoverage) {
                        currentCoverage.name = data.name;
                    }

                    break;
                default:
                    break;
            }

            this.setState({
                hasDialogChanged: false,
                isDialogSubmitting: false,
            });
        }
        catch (e) {
            this.setState({
                isDialogSubmitting: e.message || e || "Could not save changes"
            });
        }
    }

    private toggleEditRange(range: any) {
        range.isEdit = !range.isEdit;

        this.setState(s => ({
            hasDialogChanged: s.hasDialogChanged || range.isEdit,
            disallowDialogSave: s.editCoverageRanges.some(a => a.isEdit),
            editCoverageRanges: s.editCoverageRanges
        }));
    }

    private changeRangeValue(e: ChangeEvent<HTMLInputElement>, obj: any, property: string) {
        const value = e.currentTarget.value;

        obj[property] = value;

        this.setState(s => ({
            hasDialogChanged: true,
            editCoverageRanges: s.editCoverageRanges
        }));
    }

    private addCoverageRange() {
        const ranges = this.state.editCoverageRanges;

        ranges.push({
            coverageId: this.state.editCoverageId,
            start: DateTime.local().plus({ days: 1 }).startOf("week").minus({ days: 1 }).toFormat("yyyy-MM-dd"),
            end: null,
            isEdit: true
        });

        this.setState({
            hasDialogChanged: true,
            disallowDialogSave: true,
            editCoverageRanges: ranges
        });
    }

    private deleteCoverageRange(index: number) {
        const ranges = this.state.editCoverageRanges;
        ranges.splice(index, 1);

        this.setState({
            hasDialogChanged: true,

            editCoverageRanges: ranges
        });
    }

    private handleCoverageEditTabChanged(index: number) {
        let mode: EditCoverageMode;
        switch (index) {
            case 0: mode = "ranges"; break;
            case 1: mode = "slots"; break;
            case 2: mode = "details"; break;
            default: mode = "ranges"; break;
        }

        this.setState({
            editCoverageMode: mode
        });
    }

    private promptDeleteCoverage() {
        this.setState({
            isDeletionDialogOpen: true
        });
    }

    private closeDeleteDialog() {
        this.setState({
            isDeletionDialogOpen: false,
        });
    }

    private async deleteCoverage() {
        await V2.delete(`schedule/coverage/types/${this.state.editCoverageId}`);

        const coverages = this.state.coverages;
        const coverageIndex = coverages.findIndex(a => a.id === this.state.editCoverageId);
        if (coverageIndex !== -1) {
            coverages.splice(coverageIndex, 1);

            this.setState({
                coverages: coverages,
                editCoverageLoaded: false,
                editCoverageId: ""
            });
        }
    }
}

export default withTitle("Coverage Schedule", withProps(withAuth(ScheduleCoverageView), { group: WellKnownGroups.Surgeon }));
export const ScheduleTelemarketerCallView = withTitle("Patient Care Rep. On-Call Schedule", withProps(withAuth(ScheduleCoverageView), { group: WellKnownGroups.PatientCareRep }));
export const ScheduleSurgicalConsultCallView = withTitle("Surgical Consultant On-Call Schedule", withProps(withAuth(ScheduleCoverageView), { group: WellKnownGroups.SurgicalConsultant }));
